/**
 * 动物性格测试计算工具
 * 实现分数计算、归一化、L2归一化和余弦相似度匹配
 */

import { animalArchetypes } from '@/data/animalArchetypes';
import { animalScoreMap, dimensionKeys, dimensions } from '@/data/animalQuestions';

/**
 * 计算每个维度的理论最大值
 * @returns {Object} 各维度最大值对象 { DOM: number, STR: number, ... }
 */
export function calculateMaxPerDimension() {
  const maxPerDim = {};
  dimensionKeys.forEach(k => {
    maxPerDim[k] = 0;
  });
  
  animalScoreMap.forEach(questionMap => {
    const bestThisQuestion = {};
    dimensionKeys.forEach(k => {
      bestThisQuestion[k] = 0;
    });
    
    ['A', 'B', 'C', 'D'].forEach(opt => {
      const scoreObj = questionMap[opt];
      if (!scoreObj) return;
      
      Object.keys(scoreObj).forEach(dim => {
        const val = scoreObj[dim] || 0;
        if (val > bestThisQuestion[dim]) {
          bestThisQuestion[dim] = val;
        }
      });
    });
    
    dimensionKeys.forEach(dim => {
      maxPerDim[dim] += bestThisQuestion[dim];
    });
  });
  
  return maxPerDim;
}

// 全局缓存最大值（避免重复计算）
let cachedMaxPerDim = null;

/**
 * 获取缓存的最大值（如果没有则计算）
 * @returns {Object} 各维度最大值对象
 */
export function getMaxPerDimension() {
  if (!cachedMaxPerDim) {
    cachedMaxPerDim = calculateMaxPerDimension();
  }
  return cachedMaxPerDim;
}

/**
 * 计算原始维度分数
 * @param {Object} answers - 答案对象 { "1": "A", "2": "B", ... }
 * @returns {Object} 各维度原始分数 { DOM: number, STR: number, ... }
 */
export function calculateRawScores(answers) {
  const scores = {};
  dimensionKeys.forEach(k => {
    scores[k] = 0;
  });
  
  Object.keys(answers).forEach(questionId => {
    const questionIndex = parseInt(questionId) - 1;
    const selectedOption = answers[questionId];
    
    if (questionIndex < 0 || questionIndex >= animalScoreMap.length) return;
    if (!selectedOption) return;
    
    const scoreMap = animalScoreMap[questionIndex];
    const optionScores = scoreMap[selectedOption];
    
    if (!optionScores) return;
    
    dimensionKeys.forEach(dim => {
      scores[dim] += (optionScores[dim] || 0);
    });
  });
  
  return scores;
}

/**
 * 按最大值归一化
 * @param {Object} rawScores - 原始分数
 * @param {Object} maxPerDim - 各维度最大值
 * @returns {Object} 归一化分数 (0-1)
 */
export function normalizeByMax(rawScores, maxPerDim) {
  const normalized = {};
  dimensionKeys.forEach(dim => {
    const max = maxPerDim[dim] || 1;
    normalized[dim] = rawScores[dim] / max;
  });
  return normalized;
}

/**
 * 增强维度分数，突出高值（幂函数增强方案）
 * @param {Object} normalizedScores - 归一化分数 (0-1)
 * @param {Object} options - 配置选项
 * @param {number} options.power - 幂指数，默认 0.7，越小增强效果越明显
 * @param {number} options.minEnhancement - 最小增强系数，默认 1.1
 * @param {number} options.maxEnhancement - 最大增强系数，默认 1.5
 * @returns {Object} 增强后的分数 (0-1)
 */
export function enhanceDimensionScores(normalizedScores, options = {}) {
  const {
    power = 0.7,           // 幂指数，控制整体增强程度
    minEnhancement = 1.1,  // 最小增强系数
    maxEnhancement = 1.5    // 最大增强系数
  } = options;
  
  const enhanced = {};
  const values = Object.values(normalizedScores);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const maxScore = Math.max(...values);
  const minScore = Math.min(...values);
  const range = maxScore - minScore || 0.1; // 防止除零
  
  Object.keys(normalizedScores).forEach(dim => {
    const score = normalizedScores[dim];
    
    // 计算相对位置 (0-1)
    const relativePosition = (score - minScore) / range;
    
    // 计算增强系数：分数越高，增强越多
    const enhancementRatio = minEnhancement + 
      (maxEnhancement - minEnhancement) * Math.pow(relativePosition, 2);
    
    // 应用增强：使用幂函数 + 增强系数
    let enhancedScore;
    if (score > mean) {
      // 高于平均值：增强
      enhancedScore = Math.pow(score, power) * enhancementRatio;
    } else {
      // 低于平均值：极轻微压缩（更温和）
      // 使用非常温和的压缩方式，几乎保持原始值
      const compressionFactor = 1 - (mean - score) * 0.01; // 从0.03减少到0.01，压缩极轻
      enhancedScore = Math.pow(score, 0.95) * compressionFactor; // 使用0.95，几乎不压缩
    }
    
    // 确保在 [0, 1] 范围内
    enhanced[dim] = Math.max(0, Math.min(1.0, enhancedScore));
  });
  
  return enhanced;
}

/**
 * L2归一化（单位向量化）
 * @param {Object} vector - 向量对象
 * @returns {Object} L2归一化后的向量
 */
export function l2Normalize(vector) {
  let sumSq = 0;
  dimensionKeys.forEach(k => {
    sumSq += (vector[k] || 0) ** 2;
  });
  
  const norm = Math.sqrt(sumSq) || 1; // 防止除零
  
  const normalized = {};
  dimensionKeys.forEach(k => {
    normalized[k] = (vector[k] || 0) / norm;
  });
  
  return normalized;
}

/**
 * 计算余弦相似度（点积）
 * 注意: 两个向量都必须已经L2归一化
 * @param {Object} vec1 - 向量1
 * @param {Object} vec2 - 向量2
 * @returns {number} 相似度 (-1 to 1)
 */
export function cosineSimilarity(vec1, vec2) {
  let dotProduct = 0;
  dimensionKeys.forEach(k => {
    dotProduct += (vec1[k] || 0) * (vec2[k] || 0);
  });
  return dotProduct;
}

/**
 * 匹配最佳动物
 * @param {Object} normalizedUserVector - 用户的归一化向量（按最大值归一化）
 * @returns {Object} 匹配结果 { bestMatch, bestSimilarity, allMatches }
 */
export function matchAnimal(normalizedUserVector) {
  const userVectorNormalized = l2Normalize(normalizedUserVector);
  
  let bestMatch = null;
  let bestSimilarity = -Infinity;
  const allMatches = [];
  
  Object.keys(animalArchetypes).forEach(animalName => {
    const animalData = animalArchetypes[animalName];
    const animalVector = animalData.vector;
    const animalVectorNormalized = l2Normalize(animalVector);
    
    const similarity = cosineSimilarity(userVectorNormalized, animalVectorNormalized);
    
    allMatches.push({
      name: animalName,
      similarity: similarity,
      emoji: animalData.emoji
    });
    
    if (similarity > bestSimilarity) {
      bestSimilarity = similarity;
      bestMatch = animalName;
    }
  });
  
  // 按相似度排序
  allMatches.sort((a, b) => b.similarity - a.similarity);
  
  return {
    bestMatch,
    bestSimilarity,
    allMatches
  };
}

/**
 * 完整计算流程
 * @param {Object} answers - 用户答案 { "1": "A", "2": "B", ... }
 * @returns {Object} 完整结果对象
 */
export function calculateAnimalResult(answers) {
  // 1. 获取最大值（缓存）
  const maxPerDim = getMaxPerDimension();
  
  // 2. 计算原始分数
  const rawScores = calculateRawScores(answers);
  
  // 3. 按最大值归一化
  const normalizedScores = normalizeByMax(rawScores, maxPerDim);
  
  // 4. 匹配动物（使用原始归一化分数，保持匹配准确性）
  const matchResult = matchAnimal(normalizedScores);
  
  // 5. 获取动物详细信息
  const animalData = animalArchetypes[matchResult.bestMatch];
  
  // 6. 生成解释
  const interpretation = generateInterpretation({
    similarity: matchResult.bestSimilarity,
    normalizedScores
  });
  
  return {
    animal: matchResult.bestMatch,
    similarity: matchResult.bestSimilarity,
    description: animalData.description,
    emoji: animalData.emoji,
    name: animalData.name,
    traits: animalData.traits,
    careers: animalData.careers,
    compatibility: animalData.compatibility,
    color: animalData.color,
    tags: animalData.tags,
    
    dimensionScores: rawScores,
    normalizedScores: normalizedScores,
    maxPerDim: maxPerDim,
    
    allAnimals: matchResult.allMatches,
    
    interpretation: interpretation,
    
    timestamp: new Date().toISOString()
  };
}

/**
 * 生成结果解读文本
 * @param {Object} params - 参数对象
 * @param {number} params.similarity - 相似度
 * @param {Object} params.normalizedScores - 归一化分数
 * @returns {string[]} 解释文本数组
 */
export function generateInterpretation({ similarity, normalizedScores }) {
  const interpretations = [];
  
  // 基于相似度的解读
  if (similarity > 0.9) {
    interpretations.push("你与这个动物原型高度契合，这是你的核心性格特征！");
  } else if (similarity > 0.7) {
    interpretations.push("你与这个动物原型非常相似，大部分时候你都展现出这样的特质。");
  } else {
    interpretations.push("你与这个动物原型有一定相似性，这可能是你的某一面。");
  }
  
  // 基于维度的解读
  const sortedDims = dimensionKeys
    .map(k => ({ key: k, value: normalizedScores[k] }))
    .sort((a, b) => b.value - a.value);
  
  const topDim = sortedDims[0];
  const bottomDim = sortedDims[sortedDims.length - 1];
  
  const dimNames = {
    DOM: "支配性",
    STR: "策略性",
    COM: "社群性",
    SOL: "独处性",
    AGI: "敏捷性",
    SEC: "安全性",
    AES: "美学性"
  };
  
  if (topDim.value > 0.7) {
    interpretations.push(`你的${dimNames[topDim.key]}特别突出，这是你最显著的性格特征。`);
  }
  
  if (bottomDim.value < 0.3) {
    interpretations.push(`相比之下，你的${dimNames[bottomDim.key]}较低，你可能不太在意这方面。`);
  }
  
  return interpretations;
}

/**
 * 获取维度详细信息
 * @param {Object} normalizedScores - 归一化分数
 * @returns {Array} 维度详细信息数组
 */
export function getDimensionDetails(normalizedScores) {
  return dimensionKeys.map(key => ({
    key,
    name: dimensions[key].name,
    description: dimensions[key].description,
    value: normalizedScores[key],
    percentage: Math.round(normalizedScores[key] * 100)
  }));
}

